import React, {
	FC,
	ReactNode,
	useRef,
	useState,
	useEffect,
	useContext,
} from 'react'
import { createPortal } from 'react-dom'
import { Info } from '@mui/icons-material'
import cn from '../../lib/utils/cn'
import { ReactComponent as Arrow } from '../../assets/icons/interactions/select_arrow.svg'
import AppContext from '../../context/appContext'
import Tooltip from './Tooltip'

interface SelectOption {
	value: string | number
	label: string
	disabled?: boolean
	isEmpty?: boolean
	key?: string | number
}

interface SelectProps {
	options: SelectOption[] | string[]
	value?: string | number
	displayedValue?: string | number
	id?: string
	onChange: (value: string | number) => void
	label?: string | ReactNode
	placeholder?: string
	wrapperClassName?: string
	labelClassName?: string
	selectClassName?: string
	optionClassName?: string
	dropdownClassName?: string
	errorClassName?: string
	className?: string
	disabled?: boolean
	direction?: 'top' | 'bottom'
	showReset?: boolean
	onReset?: () => void
	searchable?: boolean
	required?: boolean
	error?: string
	tooltip?: string
	tooltipIconPosition?: 'left' | 'right'
}

const Select: FC<SelectProps> = ({
	options = [],
	value,
	onChange,
	label = null,
	placeholder = 'Select...',
	wrapperClassName = '',
	displayedValue = '',
	labelClassName = '',
	selectClassName = '',
	optionClassName = '',
	className = '',
	dropdownClassName = '',
	errorClassName = '',
	disabled = false,
	direction: initialDirection = 'bottom',
	showReset = false,
	onReset,
	searchable = false,
	required = false,
	error,
	tooltip,
	tooltipIconPosition = 'right',
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
	const { darkMode } = useContext(AppContext)
	const [calculatedDirection, setCalculatedDirection] = useState<
		'top' | 'bottom'
	>(initialDirection)
	const triggerRef = useRef<HTMLDivElement>(null)
	const dropdownRef = useRef<HTMLDivElement>(null)
	const openTimeoutRef = useRef<NodeJS.Timeout>()
	// const [search, setSearch] = useState('')

	const selectedOption = options?.find((opt) =>
		typeof opt === 'object' && opt !== null && 'value' in opt
			? opt.value === value
			: opt === value,
	)
	const [search, setSearch] = useState(
		searchable
			? typeof selectedOption === 'object' &&
				selectedOption !== null &&
				'label' in selectedOption
				? selectedOption.label
				: typeof selectedOption === 'string'
					? selectedOption
					: ''
			: '',
	)

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node
			if (
				triggerRef.current?.contains(target) ||
				dropdownRef.current?.contains(target)
			) {
				return
			}
			setIsOpen(false)
			setDropdownStyle({})
			setSearch('')
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	useEffect(() => {
		return () => {
			if (openTimeoutRef.current) {
				clearTimeout(openTimeoutRef.current)
			}
		}
	}, [])

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = ''
		}

		return () => {
			document.body.style.overflow = ''
		}
	}, [isOpen])

	useEffect(() => {
		if (!isOpen) return

		const updateDropdownPosition = () => {
			const trigger = triggerRef.current
			const dropdown = dropdownRef.current
			if (!trigger || !dropdown) return

			const rect = trigger.getBoundingClientRect()
			const dropdownHeight = dropdown.offsetHeight
			const windowHeight = window.innerHeight
			const spaceBelow = windowHeight - rect.bottom
			const spaceAbove = rect.top

			const shouldShowAbove =
				spaceBelow < dropdownHeight && spaceAbove > spaceBelow
			const newDirection = shouldShowAbove ? 'top' : 'bottom'
			setCalculatedDirection(newDirection)

			const top =
				newDirection === 'bottom'
					? rect.bottom + window.scrollY - 2
					: rect.top + window.scrollY - dropdownHeight + 2

			setDropdownStyle({
				top,
				left: rect.left + window.scrollX,
				width: rect.width,
				position: 'absolute' as const,
				zIndex: 9999,
			})
		}

		updateDropdownPosition()
		window.addEventListener('resize', updateDropdownPosition)
		window.addEventListener('scroll', updateDropdownPosition, true)

		return () => {
			window.removeEventListener('resize', updateDropdownPosition)
			window.removeEventListener('scroll', updateDropdownPosition, true)
		}
	}, [isOpen, options, search, value, triggerRef, dropdownRef])

	const filteredOptions = searchable
		? options.filter((opt) =>
				typeof opt === 'string'
					? opt.toLowerCase().includes(search?.toLowerCase())
					: opt.label.toLowerCase().includes(search?.toLowerCase()),
			)
		: options

	const displayedLabel = displayedValue
		? String(displayedValue)
		: typeof selectedOption === 'object' &&
			  selectedOption !== null &&
			  'label' in selectedOption
			? selectedOption.label
			: typeof selectedOption === 'string'
				? selectedOption
				: placeholder

	const tooltipIcon = Boolean(tooltip?.trim()) ? (
		<Tooltip label={tooltip} classNameChildrenWrapper="h-[18px]">
			<Info className="h-4 w-4 shrink-0 fill-stroke-dark-dark" />
		</Tooltip>
	) : null

	return (
		<div
			className={cn(
				'relative flex flex-col w-auto h-fit',
				disabled && 'opacity-60 pointer-events-none',
				wrapperClassName,
			)}
		>
			{(label || Boolean(tooltip?.trim())) && (
				<label
					className={cn(
						'text-text-main dark:text-text-main-dark font-semibold text-base mb-2 flex items-center gap-2.5',
						labelClassName,
					)}
				>
					<span className="inline-flex items-center gap-1">
						{tooltipIconPosition === 'left' && tooltipIcon}
						{label && (
							<span>
								{label}
								{required && <span className="text-accent"> *</span>}
							</span>
						)}
						{tooltipIconPosition === 'right' && tooltipIcon}
					</span>
					{error && (
						<span
							className={cn(
								'text-negative text-base text-nowrap w-fit',
								errorClassName,
							)}
						>
							{error}
						</span>
					)}
				</label>
			)}

			<div
				className={cn(
					'relative min-w-20 w-fit h-9 dark:bg-background-dark dark:text-text-table-dark',
					className,
				)}
				ref={triggerRef}
			>
				<div
					className={cn(
						'flex items-center justify-between border border-stroke-dark dark:border-stroke-light-dark bg-white dark:bg-background-dark rounded-lg cursor-pointer h-full px-4 text-base md:text-sm text-text-main box-border touch-none select-none',
						isOpen &&
							(calculatedDirection === 'bottom'
								? 'rounded-b-none'
								: 'rounded-t-none'),
						error && 'border-red-500',
						selectClassName,
					)}
					onClick={() => {
						if (!disabled) {
							setIsOpen((prev) => !prev)
							setDropdownStyle({})
							if (!isOpen) setSearch('')
						}
					}}
				>
					{searchable && isOpen ? (
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder={placeholder}
							className="m-0 w-full p-0 bg-transparent outline-none text-text-main ellipsis dark:text-text-main-dark placeholder:text-text-secondary touch-none select-none max-xs:text-base"
							autoFocus
							onFocus={(e) => {
								e.stopPropagation()
								if (openTimeoutRef.current) {
									clearTimeout(openTimeoutRef.current)
								}
								setIsOpen(true)
							}}
							onClick={(e) => e.stopPropagation()}
						/>
					) : (
						<div
							className={cn(
								'm-0 w-full p-0 bg-transparent outline-none text-text-stroke ellipsis text-start truncate font-normal dark:text-text-main-dark',
								displayedLabel == placeholder &&
									'text-stroke-dark dark:text-stroke-dark',
							)}
						>
							{displayedLabel}
						</div>
					)}
					<div className="flex items-center gap-1">
						<Arrow
							className={cn(
								'fill-stroke-dark duration-default w-2.5 ml-0.5',
								!isOpen && 'rotate-180',
							)}
						/>
					</div>
				</div>
			</div>

			{isOpen &&
				createPortal(
					<div
						ref={dropdownRef}
						style={{
							...dropdownStyle,
							border: darkMode
								? '1px solid rgb(115 115 115)'
								: '1px solid #c7c7c7',
							overflowY: 'auto',
							overflowX: 'hidden',
							maxHeight: '147px',
						}}
						className={cn(
							'select-dropdown-container bg-white dark:bg-background-dark rounded-lg border border-stroke-dark overflow-y-auto dark:border-stroke-light-dark',
							calculatedDirection === 'top'
								? 'select-dropdown-container-top'
								: 'default-shadow',
							dropdownClassName,
						)}
					>
						{filteredOptions?.length ? (
							(calculatedDirection === 'top'
								? [...filteredOptions].reverse()
								: filteredOptions
							).map((opt, index) => {
								const isString = typeof opt === 'string'
								const key = isString ? opt : (opt?.key ?? opt.value)
								const label = isString ? opt : opt.label
								const disabledOpt = isString ? false : opt.disabled
								const optValue = isString ? opt : opt.value
								return (
									<div
										key={key}
										onClick={(e) => {
											e.stopPropagation()
											if (disabledOpt) return
											onChange(optValue)
											setIsOpen(false)
											setDropdownStyle({})
											setSearch('')
										}}
										style={{
											borderBottom:
												index === filteredOptions?.length - 1
													? 'none'
													: darkMode
														? '1px solid rgb(115 115 115)'
														: '1px solid #c7c7c7',
											backgroundColor: darkMode ? '#333333' : '#ffffff',
											color: darkMode ? '#ffffff' : '#000000',
											padding: '0.5rem 1rem',
											cursor: 'pointer',
											...(disabledOpt && {
												opacity: 0.4,
												pointerEvents: 'none',
											}),
											fontFamily: 'Roboto, sans-serif',
										}}
										className={cn(
											optValue === value &&
												'bg-background-light dark:bg-background-dark dark:hover:bg-background-light-dark px-4 py-2',
											optionClassName,
										)}
									>
										{label ?? ''}
									</div>
								)
							})
						) : (
							<div
								className="text-center text-stroke-light"
								style={{ textAlign: 'center', paddingTop: 0, paddingBottom: 0 }}
							>
								<p>No options</p>
							</div>
						)}
						{showReset && (
							<div
								onClick={(e) => {
									e.stopPropagation()
									if (onReset) {
										onReset()
									}
									setIsOpen(false)
									setDropdownStyle({})
									setSearch('')
								}}
								style={{
									borderTop: '1px solid #c7c7c7',
								}}
								className={cn(
									'select-dropdown-option px-4 py-2 cursor-pointer hover:bg-background-light dark:bg-background-dark dark:hover:bg-background-light-dark',
									value === null &&
										'bg-background-light dark:bg-background-light-dark',
								)}
							>
								{placeholder}
							</div>
						)}
					</div>,
					document.body,
				)}
		</div>
	)
}

export default Select
